import { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, Input, Row, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { useApi } from 'hooks/useApi';
import FinanceInvoices from 'libs/api/finance-invoices';
import UIHelper from 'helpers/UIHelper';
import { invoiceAdapter } from './invoice.adapter';
import Helpers from '../../../../../helpers';

const integerFields = ['receipts', 'pieces', 'visitors'];

function taxed_amount(val, percent) {
  val = val.replace(',', '.');
  if (percent === 0) {
    return val;
  }
  percent = percent / 100 + 1;
  let with_taxes = parseFloat(val) * parseFloat(percent);
  return with_taxes.toString().match(/^-?\d+(?:\.\d{0,2})?/)
    ? with_taxes.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0]
    : '';
}

const totalAtv = ({ rows }) => {
  let total_taxable_vat = rows.reduce(
    (acc, i) =>
      acc +
      parseFloat(
        typeof i.taxable_vat === 'string'
          ? i.taxable_vat.replace(',', '.')
          : i.taxable_vat || 0,
      ),
    0,
  );
  let total_receipts = rows.reduce(
    (acc, i) =>
      acc +
      parseFloat(
        typeof i.receipts === 'string'
          ? i.receipts.replace(',', '.')
          : i.receipts || 0,
      ),
    0,
  );
  let atv = 0;
  if (total_taxable_vat > 0 && total_receipts > 0) {
    atv = total_taxable_vat / total_receipts;
  }
  atv = atv.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0];
  return atv.replace('.', ',');
};

const totalUpt = ({ rows }) => {
  const total_pieces = rows.reduce(
    (acc, i) =>
      acc +
      parseFloat(
        typeof i.pieces === 'string'
          ? i.pieces.replace(',', '.')
          : i.pieces || 0,
      ),
    0,
  );
  const total_receipts = rows.reduce(
    (acc, i) =>
      acc +
      parseFloat(
        typeof i.receipts === 'string'
          ? i.receipts.replace(',', '.')
          : i.receipts || 0,
      ),
    0,
  );
  let upt = 0;
  if (total_pieces > 0 && total_receipts > 0) {
    upt = total_pieces / total_receipts;
  }
  upt = upt.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0];
  return upt.replace('.', ',');
};

const totalTaxes = ({ rows }) => {
  let total_taxable = rows.reduce(
    (acc, i) =>
      acc +
      parseFloat(
        typeof i.taxable === 'string'
          ? i.taxable.replace(',', '.')
          : i.taxable || 0,
      ),
    0,
  );
  let total_taxable_vat = rows.reduce(
    (acc, i) =>
      acc +
      parseFloat(
        typeof i.taxable_vat === 'string'
          ? i.taxable_vat.replace(',', '.')
          : i.taxable_vat || 0,
      ),
    0,
  );
  total_taxable = !isNaN(total_taxable)
    ? total_taxable.toFixed(2).replace('.', ',')
    : '';
  total_taxable_vat = !isNaN(total_taxable_vat)
    ? total_taxable_vat.toFixed(2).replace('.', ',')
    : '';
  return {
    total_taxable,
    total_taxable_vat,
  };
};

const totalConv = ({ rows }) => {
  const total_receipts = rows.reduce(
    (acc, i) =>
      acc +
      parseFloat(
        typeof i.receipts === 'string'
          ? i.receipts.replace(',', '.')
          : i.receipts || 0,
      ),
    0,
  );
  const total_visitors = rows.reduce(
    (acc, i) =>
      acc +
      parseFloat(
        typeof i.visitors === 'string'
          ? i.visitors.replace(',', '.')
          : i.visitors || 0,
      ),
    0,
  );
  let conv = 0;
  if (total_receipts > 0 && total_visitors > 0) {
    conv = total_receipts / total_visitors;
  }
  conv = conv.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0];
  return conv.replace('.', ',');
};

const totalRec = ({ rows, key }) => {
  return rows.reduce((acc, i) => acc + Number(i[key]), 0);
};

const getVal = (value) => {
  return Math.trunc(
    value && value.replace ? value.replace(/[^\d.-]/g, '') : value,
  );
};

const useInvoices = ({ isAdmin }) => {
  const { singleItem, onUpdate, updateInProcess, formErrors } = useApi({
    isMainPage: false,
    api: new FinanceInvoices(),
    singleResourceKey: 'invoices',
  });
  const { t } = useTranslation();
  const [items, setItems] = useState(singleItem?.allData?.invoices || []);
  const [isWeekClosed, setIsWeekClosed] = useState(false);
  const [initialWeekClosed, setInitialWeekClosed] = useState(false);

  useEffect(() => {
    const weekClosedFromBackend = singleItem?.allData?.invoices[0]?.week_closed;
    setInitialWeekClosed(weekClosedFromBackend);
    setIsWeekClosed(weekClosedFromBackend);
  }, [singleItem?.allData?.invoices]);

  useEffect(() => {
    if (singleItem?.allData?.invoices) {
      setItems(invoiceAdapter(singleItem?.allData?.invoices));
    }
  }, [singleItem]);

  const onChangeValuesV2 = useCallback((row) => {
    return (e) => {
      const { value, name } = e.target;
      setItems((s) =>
        s.map((i) => {
          if (i.id === row.id) {
            const needChangeField = {};
            const { vat = 0 } = row.company || {};
            switch (name) {
              case 'taxable':
                needChangeField['taxable'] = value;
                needChangeField['editingTaxable'] = true;
                const taxed = taxed_amount(value, vat);
                if (taxed !== value && taxed !== '') {
                  needChangeField['taxable_vat'] = parseFloat(taxed)
                    .toFixed(2)
                    .replace('.', ',');
                  needChangeField['editingTaxable_vat'] = false;
                } else {
                  needChangeField['taxable_vat'] = taxed;
                }
                if (taxed > 0) {
                  const receipts = i.receipts;
                  if (receipts > 0) {
                    let atv = taxed / parseFloat(receipts);
                    atv = parseFloat(atv).toFixed(2);
                    needChangeField['atv'] = atv;
                  }
                }
                break;
              case 'taxable_vat':
                needChangeField['taxable_vat'] = value;
                needChangeField['editingTaxable_vat'] = true;
                break;
              case 'receipts':
                {
                  const newVal = getVal(value);
                  const rValue = Number(newVal);
                  // ATV logic start
                  if (!(Number(i.pieces) > 0 && newVal > Number(i.pieces))) {
                    needChangeField['errorFields'] = [];
                  }
                  if (rValue > 0) {
                    let taxedForAtv =
                      i.taxable_vat && i.taxable_vat.replace
                        ? i.taxable_vat.replace(',', '.')
                        : i.taxable_vat;
                    if (taxedForAtv > 0) {
                      taxedForAtv = parseFloat(taxedForAtv);
                      let atv = taxedForAtv / parseFloat(newVal);
                      atv = atv
                        .toString()
                        .match(/^-?\d+(?:\.\d{0,2})?/)[0]
                        .replace('.', ',');
                      needChangeField['atv'] = atv;
                    } else {
                      needChangeField['atv'] = '0';
                    }
                  } else {
                    needChangeField['atv'] = '0';
                  }
                  needChangeField['receipts'] = newVal;
                }
                break;
              case 'pieces':
                {
                  const newVal = getVal(value);
                  if (Number(newVal) > 0) {
                    const rec = i.receipts;
                    if (rec <= newVal) {
                      needChangeField['errorFields'] = [];
                    }
                    if (rec > 0) {
                      let upt = Number(newVal) / Number(rec);
                      upt = upt
                        .toString()
                        .match(/^-?\d+(?:\.\d{0,2})?/)[0]
                        .replace('.', ',');
                      needChangeField['upt'] = upt;
                    } else {
                      needChangeField['upt'] = '0';
                    }
                  } else {
                    needChangeField['upt'] = '0';
                  }
                  needChangeField['pieces'] = newVal;
                }
                break;
              case 'visitors':
                if (Number(value) > 0) {
                  const rec = i.receipts;
                  if (rec > 0) {
                    let conv = Number(rec) / Number(value);
                    conv = conv
                      .toString()
                      .match(/^-?\d+(?:\.\d{0,2})?/)[0]
                      .replace('.', ',');
                    needChangeField['conversion_rate'] = conv;
                  } else {
                    needChangeField['conversion_rate'] = '0';
                  }
                } else {
                  needChangeField['conversion_rate'] = '0';
                }
                needChangeField['visitors'] = getVal(value);
                break;
              default:
                break;
            }
            return {
              ...i,
              ...needChangeField,
              [name]: value,
            };
          }
          return i;
        }),
      );
    };
  }, []);

  const onBlurHandler = useCallback((row) => {
    return (e) => {
      const { value, name } = e.target;
      setItems((s) =>
        s.map((i) => {
          if (i.id === row.id) {
            const needChangeField = {};
            const { vat = 0 } = row.company || {};
            switch (name) {
              case 'taxable': {
                let formattedVal = value;
                if (value !== '') {
                  const num = parseFloat(value.replace(',', '.'));
                  if (!isNaN(num)) {
                    formattedVal = num.toFixed(2).replace('.', ',');
                  }
                } else {
                  formattedVal = '0,00';
                }
                needChangeField['taxable'] = formattedVal;
                needChangeField['editingTaxable'] = false;
                const taxed = taxed_amount(value, vat);
                if (taxed !== value && taxed !== '') {
                  needChangeField['taxable_vat'] = parseFloat(taxed)
                    .toFixed(2)
                    .replace('.', ',');
                } else {
                  needChangeField['taxable_vat'] =
                    taxed === '' ? '0,00' : taxed;
                }
                if (taxed > 0) {
                  const receipts = i.receipts;
                  if (receipts > 0) {
                    let atv = taxed / parseFloat(receipts);
                    atv = parseFloat(atv).toFixed(2);
                    needChangeField['atv'] = atv;
                  }
                }
                break;
              }
              case 'taxable_vat': {
                let formattedVal = value;
                if (value !== '') {
                  const num = parseFloat(value.replace(',', '.'));
                  if (!isNaN(num)) {
                    formattedVal = num.toFixed(2).replace('.', ',');
                  }
                } else {
                  formattedVal = '0,00';
                }
                needChangeField['taxable_vat'] = formattedVal;
                needChangeField['editingTaxable_vat'] = false;
                break;
              }
              case 'receipts': {
                const newVal = getVal(value);
                const rValue = Number(newVal);
                // ATV logic start
                if (!(Number(i.pieces) > 0 && newVal > Number(i.pieces))) {
                  needChangeField['errorFields'] = [];
                }
                if (rValue > 0) {
                  let taxedForAtv =
                    i.taxable_vat && i.taxable_vat.replace
                      ? i.taxable_vat.replace(',', '.')
                      : i.taxable_vat;
                  if (taxedForAtv > 0) {
                    taxedForAtv = parseFloat(taxedForAtv);
                    let atv = taxedForAtv / parseFloat(newVal);
                    atv = atv
                      .toString()
                      .match(/^-?\d+(?:\.\d{0,2})?/)[0]
                      .replace('.', ',');
                    needChangeField['atv'] = atv;
                  } else {
                    needChangeField['atv'] = '0';
                  }
                } else {
                  needChangeField['atv'] = '0';
                }
                needChangeField['receipts'] = newVal;
                // CONV logic end
                break;
              }
              case 'pieces': {
                const newVal = getVal(value);
                if (Number(newVal) > 0) {
                  const rec = i.receipts;
                  if (rec <= newVal) {
                    needChangeField['errorFields'] = [];
                  }
                  if (rec > 0) {
                    let upt = Number(newVal) / Number(rec);
                    upt = upt
                      .toString()
                      .match(/^-?\d+(?:\.\d{0,2})?/)[0]
                      .replace('.', ',');
                    needChangeField['upt'] = upt;
                  } else {
                    needChangeField['upt'] = '0';
                  }
                } else {
                  needChangeField['upt'] = '0';
                }
                needChangeField['pieces'] = newVal;
                break;
              }
              case 'visitors': {
                if (Number(value) > 0) {
                  const rec = i.receipts;
                  if (rec > 0) {
                    let conv = Number(rec) / Number(value);
                    conv = conv
                      .toString()
                      .match(/^-?\d+(?:\.\d{0,2})?/)[0]
                      .replace('.', ',');
                    needChangeField['conversion_rate'] = conv;
                  } else {
                    needChangeField['conversion_rate'] = '0';
                  }
                } else {
                  needChangeField['conversion_rate'] = '0';
                }
                needChangeField['visitors'] = getVal(value);
                break;
              }
              default:
                break;
            }
            return {
              ...i,
              ...needChangeField,
              [name]: value,
            };
          }
          return i;
        }),
      );
    };
  }, []);

  const onChangeCheckboxValues = useCallback((row) => {
    return (e) => {
      const { checked, name } = e.target;
      setItems((s) =>
        s.map((i) => {
          if (i.id === row.id) {
            return {
              ...i,
              [name]: checked,
              taxable: '0,00',
              taxable_vat: '0,00',
              receipts: '0',
              pieces: '0',
              visitors: '0',
              atv: '0',
              upt: '0',
              conversion_rate: '0',
            };
          }
          return i;
        }),
      );
    };
  }, []);

  const render = useCallback(
    (name) => {
      return (data, row) => {
        let keyPressCB = UIHelper.typeNumber;
        if (integerFields.includes(name)) {
          keyPressCB = UIHelper.typeInteger;
        }
        const today = new Date();
        const rowDate = new Date(row.invoice_date);
        const isFuture = rowDate > today;
        const errorStyle = row.errorFields?.includes(name)
          ? { boxShadow: 'rgb(225, 5, 5) 0px 1px 0px' }
          : {};

        let displayValue = data;
        if (name === 'taxable') {
          if (!row.editingTaxable && data !== '') {
            const num = parseFloat(data.toString().replace(',', '.'));
            if (!isNaN(num)) {
              displayValue = num.toFixed(2).replace('.', ',');
            }
          }
        }
        if (name === 'taxable_vat') {
          if (!row.editingTaxable_vat && data !== '') {
            const num = parseFloat(data.toString().replace(',', '.'));
            if (!isNaN(num)) {
              displayValue = num.toFixed(2).replace('.', ',');
            }
          }
        }
        const isDisabled = row.status === 'confirm' || row.shopping_center_closed || isFuture || row.day_off;
        return (
          <Input
            readOnly={
              isDisabled && !isAdmin
            }
            disabled={
              isDisabled && !isAdmin
            }
            onChange={
              isDisabled && !isAdmin ? undefined : onChangeValuesV2(row)
            }
            onBlur={
              isDisabled && !isAdmin ? undefined : onBlurHandler(row)
            }
            name={name}
            onKeyPress={isDisabled && !isAdmin ? undefined : keyPressCB}
            value={displayValue}
            style={errorStyle}
          />
        );
      };
    },
    [onChangeValuesV2, onBlurHandler, initialWeekClosed, isAdmin],
  );

  const getTotals = useCallback((list) => {
    const data = {
      taxable: 0,
      taxable_vat: 0,
      receipts: 0,
      pieces: 0,
      visitors: 0,
      atv: 0,
      upt: 0,
      conversion_rate: 0,
    };
    const { total_taxable, total_taxable_vat } = totalTaxes({ rows: list });
    data.taxable = total_taxable;
    data.taxable_vat = total_taxable_vat;
    data.receipts = totalRec({ rows: list, key: 'receipts' });
    data.pieces = totalRec({ rows: list, key: 'pieces' });
    data.visitors = totalRec({ rows: list, key: 'visitors' });
    data.atv = totalAtv({ rows: list });
    data.upt = totalUpt({ rows: list });
    data.conversion_rate = totalConv({ rows: list });
    return data;
  }, []);

  const columns = useMemo(() => {
    return [
      {
        title: t('Date'),
        dataIndex: 'invoice_date',
        // fixed: 'left',
        minWidth: 120,
        width: 120,
        render: (row) => UIHelper.formatedDate(row, 'dddd, DD MMMM YYYY'),
      },
      {
        title: t('Netto (€)'),
        dataIndex: 'taxable',
        width: 120,
        render: render('taxable'),
      },
      {
        title: t('Lordo (€) %'),
        dataIndex: 'taxable_vat',
        width: 120,
        render: render('taxable_vat'),
      },
      {
        // Checks
        title: t('SCONTRINI'),
        dataIndex: 'receipts',
        width: 120,
        render: render('receipts'),
      },
      {
        // Products
        title: t('PEZZI'),
        dataIndex: 'pieces',
        width: 120,
        render: render('pieces'),
      },
      {
        title: t('VISITATORI'),
        dataIndex: 'visitors',
        width: 120,
        render: render('visitors'),
      },
      {
        title: t('ATV'),
        dataIndex: 'atv',
        width: 80,
        render: (r) =>
          parseFloat(r.toString().replace(',', '.'))
            .toFixed(2)
            .replace('.', ','),
      },
      {
        title: t('UPT'),
        dataIndex: 'upt',
        width: 80,
        render: (r) =>
          parseFloat(r.toString().replace(',', '.'))
            .toFixed(2)
            .replace('.', ','),
      },
      {
        title: t('Conversion Rate'),
        dataIndex: 'conversion_rate',
        width: 140,
        render: (r) =>
          parseFloat(r.toString().replace(',', '.'))
            .toFixed(2)
            .replace('.', ','),
      },
      {
        title: 'GIORNO DI CHIUSURA',
        dataIndex: 'day_off',
        align: 'right',
        width: 100,
        render: (data, row) => (
          <Row
            gutter={16}
            justify={'center'}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              name='day_off'
              checked={data}
              disabled={
                (!isAdmin && initialWeekClosed) || row.shopping_center_closed
              }
              onChange={
                (!isAdmin && initialWeekClosed) || row.shopping_center_closed
                  ? undefined
                  : onChangeCheckboxValues(row)
              }
            />
          </Row>
        ),
      },
    ];
  }, [t, render, onChangeCheckboxValues, initialWeekClosed, isAdmin]);

  const tableSummary = (pageData) => {
    const {
      taxable,
      taxable_vat,
      receipts,
      pieces,
      visitors,
      atv,
      upt,
      conversion_rate,
    } = getTotals(pageData);
    return (
      <Table.Summary fixed>
        <Table.Summary.Row style={{ fontWeight: 'bold' }}>
          <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
          <Table.Summary.Cell>
            {Helpers.numberWithCommas(taxable)}
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {Helpers.numberWithCommas(taxable_vat)}
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {Helpers.numberWithCommas(receipts)}
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {Helpers.numberWithCommas(pieces)}
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {Helpers.numberWithCommas(visitors)}
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {Helpers.numberWithCommas(atv)}
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {Helpers.numberWithCommas(upt)}
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {Helpers.numberWithCommas(conversion_rate)}
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  const handleUpdate = () => {
    const data = {
      invoice_date: [],
      week_day: [],
      conversion: [],
      upt: [],
      atv: [],
      visitors: [],
      pieces: [],
      receipts: [],
      taxable_vat: [],
      taxable: [],
      dayoff: [],
    };

    const checksKey = 'receipts';
    const productsKey = 'pieces';
    let hasError = false;
    const failedItems = [];

    items.forEach((i) => {
      let errorFields = [];

      if (i[checksKey] > i[productsKey]) {
        errorFields.push(checksKey, productsKey);
      }

      const netto =
        parseFloat(
          typeof i.taxable === 'string'
            ? i.taxable.replace(',', '.')
            : i.taxable || 0,
        ) || 0;
      const lordo =
        parseFloat(
          typeof i.taxable_vat === 'string'
            ? i.taxable_vat.replace(',', '.')
            : i.taxable_vat || 0,
        ) || 0;
      if (lordo < netto) {
        errorFields.push('taxable', 'taxable_vat');
      }

      if (errorFields.length > 0) {
        failedItems.push({
          ...i,
          errorFields,
        });
        hasError = true;
      }
    });

    if (hasError) {
      setItems((s) => [
        ...s.map((i) => failedItems.find((f) => f.id === i.id) || i),
      ]);

      const hasTaxableError = failedItems.some(
        (item) =>
          item.errorFields.includes('taxable') ||
          item.errorFields.includes('taxable_vat'),
      );
      const hasReceiptsPiecesError = failedItems.some(
        (item) =>
          item.errorFields.includes(checksKey) &&
          item.errorFields.includes(productsKey),
      );

      let errorMessage = '';
      if (hasTaxableError && hasReceiptsPiecesError) {
        errorMessage =
          'Il campo "Lordo" deve essere maggiore del campo "Netto"; Il campo "Pezzi" deve essere uguale o maggiore del campo "Scontrini".';
      } else if (hasTaxableError) {
        errorMessage =
          'Il campo "Lordo" deve essere maggiore del campo "Netto".';
      } else if (hasReceiptsPiecesError) {
        errorMessage =
          'Il campo "Pezzi" deve essere uguale o maggiore del campo "Scontrini".';
      }

      UIHelper.openNotificationWithIcon('error', errorMessage);
      return;
    }
    // singleItem.allData?.invoices?.forEach((row, index) => {
    items?.forEach((row, index) => {
      Object.keys(data).forEach((key) => {
        if (key === 'dayoff') {
          if (row['day_off'] === true) {
            data[key] = [...(data[key] || []), index.toString()];
          }
          return;
        }
        if (key === 'conversion') {
          data[key][index] = row['conversion_rate'].toString();
          return;
        }
        data[key][index] = row[key].toString();
      });
    });
    onUpdate({ ...data, week_closed: isWeekClosed });
  };
  useEffect(() => {
    if (formErrors) {
      const values = Object.values(formErrors);
      if (values.length > 0) {
        const errorMessage = values.flat().join('\n');
        UIHelper.openNotificationWithIcon('error', errorMessage);
      }
    }
  }, [formErrors]);

  return {
    columns,
    // data: Array.isArray(singleItem?.allData?.invoices) ? singleItem?.allData?.invoices : [],
    data: Array.isArray(items) ? items : [],
    tableSummary,
    onUpdate: handleUpdate,
    updateInProcess,
    isWeekClosed,
    setIsWeekClosed,
    initialWeekClosed,
  };
};

export default useInvoices;
